import { Trans, useLingui } from "@lingui/react/macro";
import { isSupportedCountry, providerDisplayName } from "@pontimed/types";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { createFileRoute } from "@tanstack/react-router";
import { Calendar, Globe, Mail, Phone, Stethoscope } from "lucide-react";
import { useEffect, useState } from "react";

import { ServicesPausedNotice } from "@/components/booking/services-paused-notice";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { setLocale, type SupportedLocale } from "@/i18n/i18n";
import { buildWebUrl } from "@/lib/web-url";

const VALID_LANGS: SupportedLocale[] = ["ro", "en", "bg", "de", "fr", "hu", "it", "el"];

// The widget is served from the portal origin inside a third-party iframe, so
// the booking CTA must open an absolute portal URL in a new tab. Prefer the
// live origin (works for previews + local dev); fall back to the canonical
// portal host during SSR/prerender.
const PORTAL_ORIGIN =
  typeof window !== "undefined" && window.location.origin
    ? window.location.origin
    : "https://portal.pontimed.app";
const HEX_RE = /^[0-9a-fA-F]{3,8}$/;

/**
 * The `color` param, a hex without `#`. The router's parseSearch is
 * JSON-aware: an all-digit hex (`123456`) arrives as a number, and one that
 * spells a number with an exponent (`0e7490`) as that number's value (0). So a
 * number is read again from the URL as written.
 */
function hexColor(value: unknown): string | undefined {
  const raw =
    typeof value === "number" && typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("color")
      : value;
  return typeof raw === "string" && HEX_RE.test(raw) ? raw : undefined;
}

interface WidgetSearchParams {
  /** Locale override from embedder (preserves widget language without forcing parent app's locale). */
  lang?: SupportedLocale;
  /** Hex primary color tint (no leading `#`) for embedder-branded widgets. */
  color?: string;
  /** CSV of service codes to highlight as bookable. Reserved for V1.1 — currently informational. */
  services?: string;
}

interface ProviderWidgetData {
  id: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  profession?: string | null;
  photoUrl: string | null;
  grade: string | null;
  specialty: { code: string; name: Record<string, string> | string; icon?: string | null } | null;
  yearsExperience: number | null;
  ratingAvg: number | string | null;
  reviewCount: number;
  languages: string[];
  bookingMethod: string;
  bookingPhone: string | null;
  bookingEmail: string | null;
  showPhone: boolean;
  showEmail: boolean;
  isVerified: boolean;
  acceptsNewPatients: boolean;
  /** Owner paused services (`_docs/service-pause.md`) — no new bookings. */
  servicesPaused?: boolean;
  servicesPausedUntil?: string | null;
  offersVideoConsultation: boolean;
  primaryCity: string | null;
  primaryCountry: string | null;
}

export const Route = createFileRoute("/widget/book/$providerId")({
  validateSearch: (search: Record<string, unknown>): WidgetSearchParams => {
    const langRaw = typeof search.lang === "string" ? search.lang : undefined;
    const servicesRaw = typeof search.services === "string" ? search.services : undefined;
    return {
      lang:
        langRaw && VALID_LANGS.includes(langRaw as SupportedLocale)
          ? (langRaw as SupportedLocale)
          : undefined,
      color: hexColor(search.color),
      services: servicesRaw,
    };
  },
  component: WidgetBookPage,
});

function WidgetBookPage() {
  const { providerId } = Route.useParams();
  const { lang, color } = Route.useSearch();
  const { t, i18n } = useLingui();

  const [provider, setProvider] = useState<ProviderWidgetData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Locale override from embedder param.
  useEffect(() => {
    if (lang && lang !== i18n.locale) {
      void setLocale(lang);
    }
  }, [lang, i18n.locale]);

  // Apply primary color tint via CSS custom property.
  useEffect(() => {
    if (!color) return;
    const hex =
      color.length === 3
        ? color
            .split("")
            .map((c) => c + c)
            .join("")
        : color.slice(0, 6);
    if (hex.length !== 6) return;
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return;
    document.documentElement.style.setProperty("--widget-accent", `${r} ${g} ${b}`);
  }, [color]);

  // iframe-resizer postMessage on body height change.
  useEffect(() => {
    if (typeof window === "undefined" || window.parent === window) return;
    const observer = new ResizeObserver(() => {
      const height = document.body.scrollHeight;
      window.parent.postMessage(JSON.stringify({ type: "pontimed:resize", height }), "*");
    });
    observer.observe(document.body);
    return () => observer.disconnect();
  }, []);

  // Fetch provider via the same /api/providers/:providerId used by the
  // search result card prefetch (public endpoint, no auth needed).
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    apiFetch
      .get<ProviderWidgetData>(`/api/providers/${providerId}`, {
        signal: controller.signal,
        skipAuthRefresh: true,
      })
      .then((data) => setProvider(data))
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (err instanceof ApiError) {
          if (err.status === 404) setError(t`Provider not found`);
          else setError(`HTTP ${err.status}`);
        } else {
          setError(err instanceof Error ? err.message : t`Failed to load`);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [providerId, t]);

  return (
    <main className="bg-background text-foreground min-h-screen p-3 sm:p-4">
      {loading && <WidgetSkeleton />}
      {!loading && error && (
        <div className="border-destructive/20 bg-card mx-auto flex max-w-md flex-col gap-2 rounded-xl border p-5 text-center">
          <h1 className="text-foreground text-base font-semibold">
            <Trans>Couldn't load provider</Trans>
          </h1>
          <p className="text-muted-foreground text-sm">{error}</p>
        </div>
      )}
      {!loading && !error && provider && (
        <WidgetCard provider={provider} locale={i18n.locale} lang={lang} />
      )}
    </main>
  );
}

function WidgetSkeleton() {
  return (
    <div className="mx-auto max-w-md space-y-3">
      <Skeleton className="h-32 w-full rounded-xl" />
      <Skeleton className="h-12 w-full rounded-xl" />
    </div>
  );
}

function WidgetCard({
  provider,
  locale,
  lang,
}: {
  provider: ProviderWidgetData;
  locale: string;
  /** The embedder's `lang`, when it set one. */
  lang?: SupportedLocale;
}) {
  const fullName = [provider.firstName, provider.middleName, provider.lastName]
    .filter(Boolean)
    .join(" ");
  const displayName = providerDisplayName(fullName, {
    profession: provider.profession,
    locale,
  });
  const specialtyName = pickLocalized(provider.specialty?.name ?? null, locale);
  const ratingValue =
    provider.ratingAvg != null
      ? typeof provider.ratingAvg === "string"
        ? parseFloat(provider.ratingAvg)
        : provider.ratingAvg
      : 0;

  const cta = bookingCta(provider, lang);
  // "Powered by" → the public site, the provider's country in the widget's language.
  const country = provider.primaryCountry?.toLowerCase() ?? "";
  const homeUrl = buildWebUrl(
    "",
    lang ?? locale,
    isSupportedCountry(country) ? country : undefined,
  );

  return (
    <div className="border-border bg-card mx-auto max-w-md overflow-hidden rounded-2xl border shadow-sm">
      <div className="flex items-start gap-3 p-4">
        {provider.photoUrl ? (
          <img
            src={provider.photoUrl}
            alt={displayName}
            className="size-16 flex-shrink-0 rounded-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="bg-primary/10 flex size-16 flex-shrink-0 items-center justify-center rounded-full">
            <Stethoscope className="text-primary size-7" aria-hidden />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="text-foreground truncate text-base font-semibold">{displayName}</h1>
          {specialtyName && (
            <p className="text-muted-foreground truncate text-sm">{specialtyName}</p>
          )}
          {provider.primaryCity && (
            <p className="text-muted-foreground mt-0.5 truncate text-xs">
              {provider.primaryCity}
              {provider.primaryCountry ? ` · ${provider.primaryCountry}` : ""}
            </p>
          )}
        </div>
      </div>

      {/* Trust line */}
      {(provider.isVerified || ratingValue > 0 || provider.yearsExperience) && (
        <div className="border-border flex flex-wrap items-center gap-2 border-t px-4 py-2">
          {provider.isVerified && (
            <span className="text-primary bg-primary/10 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold">
              <Trans>Verified</Trans>
            </span>
          )}
          {ratingValue > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
              ★ {ratingValue.toFixed(1)} ({provider.reviewCount})
            </span>
          )}
          {provider.yearsExperience != null && provider.yearsExperience > 0 && (
            <span className="text-muted-foreground text-xs">
              <Trans>{provider.yearsExperience}y experience</Trans>
            </span>
          )}
          {provider.languages.length > 0 && (
            <span className="text-muted-foreground inline-flex flex-wrap gap-1 text-xs uppercase">
              {provider.languages.slice(0, 4).map((lang) => (
                <span key={lang} className="bg-muted rounded px-1.5 py-0.5">
                  {lang}
                </span>
              ))}
            </span>
          )}
        </div>
      )}

      {/* Acceptance status — a pause says until when; it replaces the
          "not accepting new patients" line. */}
      {provider.servicesPaused === true ? (
        <ServicesPausedNotice
          pausedUntil={provider.servicesPausedUntil}
          className="border-border border-t py-6"
        />
      ) : (
        !provider.acceptsNewPatients && (
          <p className="text-muted-foreground border-border border-t px-4 py-2 text-xs">
            <Trans>This provider is not accepting new patients right now.</Trans>
          </p>
        )
      )}

      {/* CTA — phone/email/external dispatch (none left while paused → no row) */}
      {(cta.kind !== "none" || provider.servicesPaused !== true) && (
        <div className="border-border bg-card border-t p-4">
          {cta.kind === "tel" && (
            <a href={cta.href} className="block">
              <Button className="h-12 w-full text-sm font-semibold">
                <Phone className="size-4" aria-hidden />
                <span className="ml-1.5">
                  <Trans>Call to book</Trans>
                </span>
              </Button>
            </a>
          )}
          {cta.kind === "mailto" && (
            <a href={cta.href} className="block">
              <Button className="h-12 w-full text-sm font-semibold">
                <Mail className="size-4" aria-hidden />
                <span className="ml-1.5">
                  <Trans>Email to book</Trans>
                </span>
              </Button>
            </a>
          )}
          {cta.kind === "external" && (
            <a href={cta.href} target="_blank" rel="noopener noreferrer" className="block">
              <Button className="h-12 w-full text-sm font-semibold">
                <Calendar className="size-4" aria-hidden />
                <span className="ml-1.5">
                  <Trans>Book on Pontimed</Trans>
                </span>
              </Button>
            </a>
          )}
          {cta.kind === "none" && (
            <p className="text-muted-foreground text-center text-xs">
              <Trans>Booking not available right now.</Trans>
            </p>
          )}
        </div>
      )}

      {/* Branding */}
      <div className="border-border bg-muted/30 flex items-center justify-center gap-1 border-t px-4 py-2">
        <Globe className="text-muted-foreground size-3" aria-hidden />
        <a
          href={homeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:text-foreground text-[10px] font-medium tracking-wide uppercase"
        >
          <Trans>Powered by Pontimed</Trans>
        </a>
      </div>
    </div>
  );
}

type BookingCta =
  | { kind: "tel"; href: string }
  | { kind: "mailto"; href: string }
  | { kind: "external"; href: string }
  | { kind: "none" };

/**
 * Dispatch the book CTA based on provider's bookingMethod (matches legacy
 * widget behavior — phone/email open native handlers; "calendar"/"online"
 * route to Pontimed for full flow). MVP doesn't host the BookingFlow
 * component yet (V1.1 — multi-step date/time picker + Stripe Elements
 * pre-pay), so "external" opens new tab to apps/app provider page where
 * the future flow will live.
 */
function bookingCta(provider: ProviderWidgetData, lang?: SupportedLocale): BookingCta {
  if (!provider.acceptsNewPatients) return { kind: "none" };
  // Paused: no PontiMed booking link; phone / email keep their own rules.
  const paused = provider.servicesPaused === true;
  if (provider.bookingMethod === "phone" && provider.bookingPhone && provider.showPhone) {
    return { kind: "tel", href: `tel:${provider.bookingPhone}` };
  }
  if (provider.bookingMethod === "email" && provider.bookingEmail) {
    return { kind: "mailto", href: `mailto:${provider.bookingEmail}` };
  }
  if (paused) return { kind: "none" };
  // calendar / online / unknown → external Pontimed link in new tab. The new
  // tab is first-party: the language this iframe set (cookie, storage) stays
  // partitioned under the embedding site, so it travels in the URL.
  return {
    kind: "external",
    href: `${PORTAL_ORIGIN}/book/${provider.id}${lang ? `?lang=${lang}` : ""}`,
  };
}

function pickLocalized(
  value: Record<string, string> | string | null | undefined,
  locale: string,
): string | null {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value) as Record<string, string>;
      return parsed[locale] ?? parsed.ro ?? parsed.en ?? null;
    } catch {
      return value;
    }
  }
  return value[locale] ?? value.ro ?? value.en ?? null;
}
